import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";

import Navbar from "./components/common/Navbar";
import Footer from "./components/common/Footer";
import ProtectedRoute from "./components/common/ProtectedRoute";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { LoadingState } from "./components/common/ui";

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Brokers = lazy(() => import("./pages/Brokers"));
const Strategies = lazy(() => import("./pages/Strategies"));
const Legs = lazy(() => import("./pages/Legs"));
const Reports = lazy(() => import("./pages/Reports"));
const Bots = lazy(() => import("./pages/Bots"));
const Profile = lazy(() => import("./pages/Profile"));
const SystemHealth = lazy(() => import("./pages/SystemHealth"));
const Home = lazy(() => import("./pages/Home"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const RiskDisclaimer = lazy(() => import("./pages/RiskDisclaimer"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));

const protectedRoutes = [
  { path: "/dashboard", element: <Dashboard /> },
  { path: "/brokers", element: <Brokers /> },
  { path: "/bots", element: <Bots /> },
  { path: "/strategies", element: <Strategies /> },
  { path: "/legs", element: <Legs /> },
  { path: "/reports", element: <Reports /> },
  { path: "/profile", element: <Profile /> },
  { path: "/system-health", element: <SystemHealth /> },
];

const withProtection = (element) => <ProtectedRoute>{element}</ProtectedRoute>;

function Layout() {
  const location = useLocation();
  const hideNavbar = location.pathname === "/login" || location.pathname === "/register";
  const isPublicPage = [
    "/",
    "/about",
    "/contact",
    "/privacy-policy",
    "/terms-and-conditions",
    "/risk-disclaimer",
  ].includes(location.pathname);

  return (
    <>
      {!hideNavbar && <Navbar />}
      <div className={location.pathname === "/" ? "w-full" : "container mx-auto"}>
        <Suspense fallback={<LoadingState message="Loading view..." height="400px" />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/risk-disclaimer" element={<RiskDisclaimer />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            {protectedRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={withProtection(element)} />
            ))}
          </Routes>
        </Suspense>
      </div>
      {isPublicPage && location.pathname !== "/" && <Footer />}
    </>
  );
}

export default function App() {
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "dark";
    document.documentElement.setAttribute("data-theme", savedTheme);
    if (savedTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <Layout />
      </ErrorBoundary>
    </BrowserRouter>
  );
}
